export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok)
    throw new ApiError(res.status, (json["error"] as string) ?? `${res.status} ${res.statusText}`);
  return json as T;
}

export async function apiBytes(path: string): Promise<Uint8Array> {
  const res = await fetch(path, { credentials: "same-origin" });
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`);
  return new Uint8Array(await res.arrayBuffer());
}

export interface Me {
  user: { id: string; role: "owner" | "confidant"; name: string; groupPublicKey: string | null };
  vaults: MeVault[];
  isAdmin: boolean;
}
export type VaultMode = "kid" | "guardian" | "circle";

export interface MeVault {
  id: string;
  name: string;
  mode: VaultMode;
  k: number;
  n: number;
  windowMinutes: number;
  publicKey: string;
  metadataPublicKey: string;
  role: "owner" | "confidant";
  sealed: boolean;
  sealedMetadataSecret: string | null;
  /** Kid mode only: the parent's sealed share. */
  sealedShareForOwner: string | null;
  confidant: { id: string; status: string; shareIndex: number; sealedShare: string | null } | null;
}
export interface AuthStatus {
  setup: { storageConfigured: boolean; hasOwner: boolean; storageKind: string };
  user: Me["user"] | null;
}
export interface VaultDetail {
  vault: {
    id: string;
    name: string;
    mode: VaultMode;
    k: number;
    n: number;
    windowMinutes: number;
    publicKey: string;
    metadataPublicKey: string;
    sealed: boolean;
    unclaimedShares: number;
    createdAt: string;
  };
  confidants: {
    id: string;
    name: string;
    status: "invited" | "enrolled" | "verified";
    shareIndex: number;
    inviteExpiresAt: string | null;
    groupPublicKey: string | null;
    fingerprint: string | null;
    enrolledAt: string | null;
    verifiedAt: string | null;
    lastSeenAt: string | null;
  }[];
  devices: {
    id: string;
    name: string;
    caps: string[];
    createdAt: string;
    lastSeenAt: string | null;
    revokedAt: string | null;
    connected: boolean;
  }[];
  health: {
    sealed: boolean;
    enrolled: number;
    verified: number;
    needed: number;
    total: number;
    unclaimedShares: number;
    ok: boolean;
  };
}
